import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { workoutAPI } from '../services/workoutAPI';
import ExerciseForm from './ExerciseForm';

function ExerciseLibrary({ onBack }) {
  const [exercises, setExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  useEffect(() => {
    fetchExercises();
  }, [showArchived]);

  const fetchExercises = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workoutAPI.fetchExercises(showArchived);
      setExercises(data);
    } catch (err) {
      console.error('Error fetching exercises:', err);
      setError('Failed to load exercises. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingExercise(null);
    setShowForm(true);
  };

  const handleEdit = (exercise) => {
    setEditingExercise(exercise);
    setShowForm(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    setError(null);
    try {
      if (editingExercise) {
        await workoutAPI.updateExercise(editingExercise.id, formData);
      } else {
        await workoutAPI.createExercise(formData);
      }
      setShowForm(false);
      setEditingExercise(null);
      await fetchExercises();
    } catch (err) {
      console.error('Error saving exercise:', err);

      // Check for type change forbidden error
      if (err.code === 'TYPE_CHANGE_FORBIDDEN') {
        setError(err.message || 'Cannot change exercise type: exercise has existing workout logs.');
      } else {
        setError('Failed to save exercise. Please try again.');
      }

      // Don't close modal on error - let user see the error and fix it
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (exerciseId, isArchived) => {
    if (!archiveConfirm || archiveConfirm !== exerciseId) {
      setArchiveConfirm(exerciseId);
      return;
    }

    setError(null);
    try {
      if (isArchived) {
        await workoutAPI.unarchiveExercise(exerciseId);
      } else {
        await workoutAPI.archiveExercise(exerciseId);
      }
      setArchiveConfirm(null);
      await fetchExercises();
    } catch (err) {
      console.error('Error archiving/unarchiving exercise:', err);
      setError(`Failed to ${isArchived ? 'restore' : 'archive'} exercise. Please try again.`);
    }
  };

  const filteredExercises = useMemo(() => {
    return exercises.filter(exercise => {
      // Filter by archived status
      const matchesArchiveFilter = showArchived ? exercise.is_archived === 1 : exercise.is_archived === 0;
      const matchesSearch = exercise.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !categoryFilter || exercise.category === categoryFilter;
      const matchesType = !typeFilter || exercise.exercise_type === typeFilter;
      return matchesArchiveFilter && matchesSearch && matchesCategory && matchesType;
    });
  }, [exercises, searchTerm, categoryFilter, typeFilter, showArchived]);

  const categories = useMemo(() => {
    const cats = [...new Set(exercises.map(ex => ex.category).filter(Boolean))];
    return cats.sort();
  }, [exercises]);

  // Create options for category filter dropdown
  const categoryOptions = useMemo(() => {
    return categories.map(cat => ({ value: cat, label: cat }));
  }, [categories]);

  // Selected category for react-select
  const selectedCategory = categoryFilter
    ? { value: categoryFilter, label: categoryFilter }
    : null;

  const exerciseTypeLabels = {
    'weight_reps': 'Weight & Reps',
    'weight_time': 'Weight & Time',
    'cardio_basic': 'Cardio Basic',
    'cardio_segments': 'Cardio Intervals'
  };

  // Options for type filter dropdown
  const typeOptions = [
    { value: 'weight_reps', label: 'Weight & Reps' },
    { value: 'weight_time', label: 'Weight & Time' },
    { value: 'cardio_basic', label: 'Cardio Basic' },
    { value: 'cardio_segments', label: 'Cardio Intervals' }
  ];

  // Selected type for react-select
  const selectedType = typeFilter
    ? typeOptions.find(opt => opt.value === typeFilter)
    : null;

  const exerciseTypeBadgeColors = {
    'weight_reps': 'primary',
    'weight_time': 'info',
    'cardio_basic': 'success',
    'cardio_segments': 'warning'
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading exercises...</p>
      </div>
    );
  }

  return (
    <div className="exercise-library">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Exercise Library</h2>
          <p className="text-muted mb-0">Manage your exercise collection</p>
        </div>
        <div>
          <Button variant="secondary" onClick={onBack} className="me-2">
            <i className="fas fa-arrow-left me-1"></i> Back
          </Button>
          <Button variant="primary" onClick={handleCreateNew}>
            <i className="fas fa-plus me-1"></i> New Exercise
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-md-6">
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search exercises..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </div>
        <div className="col-md-3">
          <Form.Group>
            <Form.Label>Category</Form.Label>
            <Select
              options={categoryOptions}
              value={selectedCategory}
              onChange={(option) => setCategoryFilter(option ? option.value : '')}
              placeholder="All Categories"
              isSearchable={true}
              isClearable={true}
            />
          </Form.Group>
        </div>
        <div className="col-md-3">
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Select
              options={typeOptions}
              value={selectedType}
              onChange={(option) => setTypeFilter(option ? option.value : '')}
              placeholder="All Types"
              isSearchable={true}
              isClearable={true}
            />
          </Form.Group>
        </div>
      </div>

      {/* Archive Toggle */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="showArchivedToggle"
            checked={showArchived}
            onChange={(e) => {
              setShowArchived(e.target.checked);
              setArchiveConfirm(null); // Reset confirmation
            }}
          />
          <label className="form-check-label" htmlFor="showArchivedToggle">
            {showArchived ? (
              <>
                <i className="fas fa-archive me-1"></i>
                Showing Archived Exercises
              </>
            ) : (
              <>
                <i className="fas fa-list me-1"></i>
                Showing Active Exercises
              </>
            )}
          </label>
        </div>
      </div>

      {/* Exercise Table */}
      {filteredExercises.length === 0 ? (
        <div className="text-center py-5">
          <i className={`fas ${showArchived ? 'fa-archive' : 'fa-dumbbell'} fa-3x text-muted mb-3`}></i>
          <p className="text-muted">
            {exercises.length === 0
              ? 'No exercises yet. Create your first exercise to get started!'
              : showArchived
              ? 'No archived exercises. Exercises you archive will appear here.'
              : 'No exercises match your filters.'}
          </p>
          {exercises.length === 0 && !showArchived && (
            <Button variant="primary" onClick={handleCreateNew}>
              <i className="fas fa-plus me-1"></i> Create First Exercise
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="mb-2 text-muted">
            Showing {filteredExercises.length} of {exercises.length} {showArchived ? 'archived' : 'active'} exercises
          </div>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Category</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExercises.map(exercise => (
                <tr key={exercise.id}>
                  <td>
                    <strong>{exercise.name}</strong>
                    {exercise.is_shared === 1 && (
                      <Badge bg="secondary" className="ms-2">Shared</Badge>
                    )}
                    {exercise.is_archived === 1 && (
                      <Badge bg="warning" className="ms-2">Archived</Badge>
                    )}
                  </td>
                  <td>
                    <Badge bg={exerciseTypeBadgeColors[exercise.exercise_type]}>
                      {exerciseTypeLabels[exercise.exercise_type]}
                    </Badge>
                  </td>
                  <td>{exercise.category || '-'}</td>
                  <td className="text-truncate" style={{ maxWidth: '200px' }}>
                    {exercise.notes || '-'}
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(exercise)}
                      className="me-2"
                      disabled={exercise.is_shared === 1 || exercise.is_archived === 1}
                    >
                      <i className="fas fa-edit"></i>
                    </Button>

                    {exercise.is_archived === 1 ? (
                      // Unarchive button for archived exercises
                      archiveConfirm === exercise.id ? (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleArchive(exercise.id, true)}
                            className="me-2"
                          >
                            Confirm Restore?
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setArchiveConfirm(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleArchive(exercise.id, true)}
                          title="Restore exercise"
                        >
                          <i className="fas fa-undo"></i>
                        </Button>
                      )
                    ) : (
                      // Archive button for active exercises
                      archiveConfirm === exercise.id ? (
                        <>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleArchive(exercise.id, false)}
                            className="me-2"
                            disabled={exercise.is_shared === 1}
                          >
                            Confirm Archive?
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setArchiveConfirm(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleArchive(exercise.id, false)}
                          disabled={exercise.is_shared === 1}
                          title="Archive exercise"
                        >
                          <i className="fas fa-archive"></i>
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* Exercise Form Modal */}
      <ExerciseForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingExercise(null);
        }}
        exercise={editingExercise}
        onSave={handleSave}
        isLoading={isSaving}
      />
    </div>
  );
}

export default ExerciseLibrary;
